/**
 * Client diagnostics: script errors, rejected promises and connection closes are written to the
 * console and sent to the realm's log (`/api/client-log`), so a dropped game can be explained
 * from the server side. At most a few reports per minute; nothing about the player's files.
 */

const MAX_PER_MINUTE = 10;
let sent = 0;
let windowStart = 0;
let endpoint: string | null = null;

/** Where reports go: the realm's host (which may differ from the page's). */
function url(): string {
  if (endpoint) return endpoint;
  const env = (import.meta as unknown as { env?: Record<string, string> }).env;
  const realm = env?.VITE_REALM_URL;
  if (realm) {
    const u = new URL(realm);
    u.protocol = u.protocol === 'wss:' ? 'https:' : 'http:';
    u.pathname = '/api/client-log';
    endpoint = u.href;
  } else endpoint = '/api/client-log';
  return endpoint;
}

/** Logs a diagnostic line and sends it to the realm. */
export function report(kind: string, detail: string): void {
  const line = `${kind}: ${detail}`;
  console.warn(`[d2] ${line}`);
  const now = Date.now();
  if (now - windowStart > 60_000) {
    windowStart = now;
    sent = 0;
  }
  if (++sent > MAX_PER_MINUTE) return;
  try {
    const body = line.slice(0, 2000);
    if (!navigator.sendBeacon?.(url(), body)) void fetch(url(), { method: 'POST', body, keepalive: true }).catch(() => {});
  } catch {
    // Diagnostics never get in the way of the game.
  }
}

/** Readable description of an error value. */
export function errorText(e: unknown): string {
  if (e instanceof Error) return `${e.message}${e.stack ? ` | ${e.stack.split('\n').slice(1, 4).map((s) => s.trim()).join(' | ')}` : ''}`;
  return String(e);
}

/** Catches what nothing else caught: uncaught exceptions and unhandled promise rejections. */
export function installErrorReporting(): void {
  window.addEventListener('error', (e) => report('error', e.error ? errorText(e.error) : `${e.message} @ ${e.filename}:${e.lineno}`));
  window.addEventListener('unhandledrejection', (e) => report('unhandled rejection', errorText(e.reason)));
}
